import axios from "axios";
import React, { createContext, useContext, useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

const SCHOOL_INFO_URL = "api/school-information";

const SchoolContext = createContext();

export function useSchoolContext() {
  return useContext(SchoolContext);
}

export const SchoolConsumer = SchoolContext.Consumer;

export function SchoolContextProvider({ schoolUIEvents, children }) {
  const [ACTIVES, SETACTIVES] = useState([]);
  const [schoolData, setSchoolData] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [refetch, setRefetch] = useState({
    schools: true,
    actives: true,
  });

  const state_user = useSelector((state) => state.auth.user, shallowEqual);
  const current_branch = useSelector(
    (state) => state.auth.branches.currentBranch,
    shallowEqual
  );

  useEffect(() => {
    const getSchoolListData = async () => {
      setLoading(true);
      try {
        // Get schools for current branch
        const branchId = current_branch?.branch_id;
        if (!branchId) {
          setSchoolData({ results: [] });
          return;
        }

        const res_school_list = await axios(
          `${SCHOOL_INFO_URL}/branch/${branchId}`
        );
        setSchoolData(res_school_list.data);
      } catch (error) {
        console.error("Error fetching school data:", error);
        setSchoolData({ results: [] });
      }
    };

    const refetchRequest = async () => {
      setLoading(true);

      try {
        // Simple active/inactive options for schools
        const activeOptions = [
          { value: 1, label: "Active", title: "Active" },
          { value: 0, label: "Inactive", title: "Inactive" },
        ];

        if (refetch.actives) {
          SETACTIVES(activeOptions);
        }
      } catch (e) {
        console.error("Error in refetch:", e);
      }
    };

    if (state_user && refetch && current_branch) {
      refetchRequest()
        .then(() => {
          refetch.actives = false;
          getSchoolListData().then(() => {
            refetch.schools = false;
            setLoading(false);
          });
        })
        .catch((e) => {
          console.error("Error in refetch flow:", e);
          setLoading(false);
        });
    }
  }, [refetch, state_user, current_branch]);

  // School CRUD operations
  const createSchool = async (schoolData) => {
    setLoading(true);
    try {
      // Automatically set the current branch
      const dataWithBranch = {
        ...schoolData,
        branch_id: current_branch.branch_id,
      };

      const response = await axios.post(
        `${SCHOOL_INFO_URL}/create`,
        dataWithBranch
      );

      // Trigger refetch to update the list
      setRefetch((prev) => ({ ...prev, schools: true }));

      return response.data;
    } catch (error) {
      console.error("Error creating school:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateSchool = async (schoolId, schoolData) => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${SCHOOL_INFO_URL}/edit/${schoolId}`,
        schoolData
      );

      // Trigger refetch to update the list
      setRefetch((prev) => ({ ...prev, schools: true }));

      return response.data;
    } catch (error) {
      console.error("Error updating school:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const activateSchool = async (schoolId) => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${SCHOOL_INFO_URL}/activate/${schoolId}`
      );

      // Trigger refetch to update the list
      setRefetch((prev) => ({ ...prev, schools: true }));

      return response.data;
    } catch (error) {
      console.error("Error activating school:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deactivateSchool = async (schoolId) => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${SCHOOL_INFO_URL}/deactivate/${schoolId}`
      );

      // Trigger refetch to update the list
      setRefetch((prev) => ({ ...prev, schools: true }));

      return response.data;
    } catch (error) {
      console.error("Error deactivating school:", error);
      const errorMessage =
        error.response?.data?.error || "Failed to deactivate school";
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getSchoolById = async (schoolId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${SCHOOL_INFO_URL}/${schoolId}`);
      return response.data.school;
    } catch (error) {
      console.error("Error fetching school:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const searchSchools = async (keyword) => {
    setLoading(true);
    try {
      const response = await axios.get(`${SCHOOL_INFO_URL}/search/${keyword}`);
      return response.data.schools;
    } catch (error) {
      console.error("Error searching schools:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const schoolContext = {
    // UI Events from props
    newSchoolButtonClick: schoolUIEvents.newSchoolButtonClick,
    openEditSchoolPage: schoolUIEvents.openEditSchoolPage,
    openActivateSchoolDialog: schoolUIEvents.openActivateSchoolDialog,
    openDeleteSchoolDialog: schoolUIEvents.openDeleteUserDialog, // You might want to rename this

    // Data
    schoolData: schoolData,
    isLoading: isLoading,
    setLoading: setLoading,
    refetch: refetch,
    setRefetch: setRefetch,
    currentBranch: current_branch,

    // Options for dropdowns/filters
    ACTIVE: {
      ACTIVES,
      SETACTIVES,
    },

    // Status options for schools
    STATUS: [
      { value: 1, label: "Active", title: "Active" },
      { value: 0, label: "Inactive", title: "Inactive" },
    ],

    // CRUD operations
    createSchool,
    updateSchool,
    activateSchool,
    deactivateSchool,
    getSchoolById,
    searchSchools,
  };

  return (
    <SchoolContext.Provider value={schoolContext}>
      {children}
    </SchoolContext.Provider>
  );
}
